import {Component, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {AccountDto, AccountRole, LoadingState} from 'common';
import {BaseComponent} from '../base.component';
import {HttpClient} from '@angular/common/http';
import {TimerComponent} from '../timer/timer.component';
import {TwoFaLoginDto} from '../../dtos/twoFaLoginDto';
import {AuthenticationService} from '../../authentication.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BaseComponent implements OnInit {
    public readonly loginModes: typeof LoginMode = LoginMode;

    private readonly TOKEN_LENGTH = 6;

    private _loginForm: FormGroup;
    public loadingState: LoadingState;
    public loginMode: LoginMode;
    public twoFaCodeText: string;
    public twoFaCodeTextHint: string;
    public twoFaCodeLength: number;
    public showExpiredText = false;
    public canResend = false;

    @ViewChild(TimerComponent) timerComponent!: TimerComponent;

    constructor(private httpClient: HttpClient, private _fb: FormBuilder, private _route: ActivatedRoute) {
        super();
    }

    ngOnInit(): void {
        // if user is logged in take them home
        if (this.userAccount !== null) {
            this.navigation.goToHome();
        }

        this.analyticsSwitchPage('Login', undefined, '/login');

        this._loginForm = this._fb.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });
        this.loadingState = LoadingState.LOADED;
        this.loginMode = LoginMode.LOGIN;
        this.twoFaCodeText = '';
        this.twoFaCodeTextHint = 'Enter the 6-digits code';
        this.twoFaCodeLength = this.TOKEN_LENGTH;
    }

    get email(): AbstractControl {
        return this._loginForm.get('email');
    }

    get password(): AbstractControl {
        return this._loginForm.get('password');
    }

    get loginForm(): FormGroup {
        return this._loginForm;
    }

    onTwoFaCodeSubmit(): void {
        this.authenticationService.loginTwoFa(this.email.value, this.password.value, new TwoFaLoginDto(this.twoFaCodeText))
            .subscribe(
                account => {
                    this.loadingState = LoadingState.LOADED;
                    switch (account.accountRoleType) {
                        case AccountRole.ADMIN:
                            console.log('TwoFaLogin successful.');
                            this.navigation.goToAdminHome();
                            break;
                        default:
                            this.notification.info('Unauthorized');
                            this.authenticationService.logout();
                            break;
                    }
                },
                error => {
                    this.loadingState = LoadingState.LOADED;
                    this.notification.info(`Login Failed: ${error.statusText} (${error.status})`);
                });
    }

    onBackPressed(): void {
        this.loginMode = LoginMode.LOGIN;
        this.twoFaCodeText = '';
        this.showExpiredText = false;
        this.canResend = false;
    }

    onTwoFaCodeValueChange(value: string): void {
        this.twoFaCodeText = value;
        if (this.twoFaCodeText.length === this.TOKEN_LENGTH) {
            this.onTwoFaCodeSubmit();
        }
    }

    onTwoFaCodeTimerComplete(): void {
        this.canResend = true;
        this.showExpiredText = true;
    }

    onResendTokenPressed(): void {
        this.loadingState = LoadingState.LOADING;
        this.canResend = false;

        this.authenticationService.loginObservable(this.email.value, this.password.value)
            .subscribe(response => {
            this.notification.info('A new code has been sent to ' + this.email.value);
            this.showExpiredText = false;
            this.timerComponent.restartTimer();
            this.loadingState = LoadingState.LOADED;
        });
    }


    canSubmit(): boolean {
        return this.twoFaCodeText.length === this.twoFaCodeLength;
    }

    onFormSubmit(): void {
        this.loadingState = LoadingState.LOADING;
        this.authenticationService.loginObservable(this.email.value, this.password.value)
            .subscribe(response => {
                if (response.token === AuthenticationService.TWO_FA_RESPONSE) {
                    this.loginMode = LoginMode.TWO_FA;
                    this.loadingState = LoadingState.LOADED;
                } else {
                    this.authenticationService.loginManager(this.email.value, this.password.value)
                        .subscribe(
                        account => {
                            this.loadingState = LoadingState.LOADED;
                            switch (account.accountRoleType) {
                                case AccountRole.MANAGER:
                                    console.log('Login successful.');
                                    this.navigation.goToAdminHome();
                                    break;
                                default:
                                    this.notification.info('Unauthorized');
                                    this.authenticationService.logout();
                                    break;
                            }
                        },
                        error => {
                            this.loadingState = LoadingState.LOADED;
                            this.notification.info(`Login Failed: ${error.statusText} (${error.status})`);
                        });
            }
        } ,
            error => {
                this.loadingState = LoadingState.LOADED;
                this.notification.info(`Login Failed: ${error.statusText} (${error.status})`);
            });
    }

    /**
     * Returns the currently logged in account
     */
    public get userAccount(): AccountDto {
        return this.authenticationService.account;
    }
}

export enum LoginMode {
    LOGIN,
    TWO_FA
}
